import { useEffect, useState, useCallback, useRef, useMemo, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import moment from 'moment'
import Breadcrumbs from '@/components/Breadcrumbs/index.jsx'
import PageTitle from '@/components/PageTitle/index'
import MasterDataTable from '@/components/MasterDataTable/index'
import Search from '@/components/Search/index'
import InputDropdown from '@/components/InputDropdown/index'
import usePage from '@/hooks/use-page'
import { getSubmittalListing, getSubmittalFilters } from '@/services/submittalService'
import { downloadSubmittalAsCsv } from './utils/downloadSubmittalAsCsv'
import { SUBMITTAL_STATUS_LABEL, isSecondStageStatus, statusLabel } from './constants'
import SubmittalStatusTag from './components/SubmittalStatusTag'
import useDataElements from '@/hooks/useDataElements'
import { labelFor } from './listing-config'
import useListingTabState from '@/hooks/useListingTabState'
import IconInfoEmpty from '@/assets/svgs/Icon-info.svg?react'
import { ToastContext } from '@/context/ToastContext'

const PAGE_LIMIT = 10

const TABLE_HEADERS = {
  data_element_id: 'Data Element',
  submission_month: 'Submission Month',
  modified: 'Last Modified',
  project_contract_name: 'Project Contract Name',
  status: 'Status',
  action: '',
}

const FILTER_FIELDS = [
  {
    key: 'data_element_id',
    label: 'Data Element',
    placeholder: 'Select Data Element',
    optionKey: 'data_element_ids',
  },
  {
    key: 'project_contract_name',
    label: 'Project Contract Name',
    placeholder: 'Select Project Contract Name',
    optionKey: 'project_contract_names',
  },
  {
    key: 'submission_month',
    label: 'Submission Month',
    placeholder: 'Select Submission Month',
    optionKey: 'submission_months',
  },
  { key: 'status', label: 'Status', placeholder: 'Select Status', optionKey: 'statuses' },
]

const toDropdownOptions = items =>
  items.map((item, index) => ({ id: String(index), label: item, value: item }))

/**
 * The statuses worth offering.
 *
 * The filters endpoint lists them straight from the service's enum, so it offers
 * the second-stage ones even to a data element with no second approver, where
 * they cannot occur. Only filtered when the listing is scoped to one element —
 * unscoped, the rows may come from an element that does have a second stage.
 */
const statusOptionsFrom = (statuses, dataElementId, secondaryApproval) =>
  statuses.filter(
    status => !dataElementId || secondaryApproval?.required || !isSecondStageStatus(status)
  )

/**
 * The Environmental Monthly Submissions listing: hardcoded columns and filters,
 * predating `listingConfig`.
 *
 * `dataElementId` scopes it to one data element, which only the tabbed shell
 * passes — one tab among several must not show another tab's submittals. Scoped,
 * the Data Element dropdown goes too: it could only ever select the one value.
 * `embedded` drops the page header, which the shell renders once above every tab.
 *
 * Nothing else changes between the two. This listing serves EMS in production, so
 * its filter row, search field and timings stay as they are even where pitstop
 * draws its own V1 tab differently — matching pitstop is not worth altering a
 * live view. The configured listing beside it is the one that follows pitstop.
 */
const SubmittalsListingPage = ({
  dataElementId = null,
  secondaryApproval = undefined,
  embedded = false,
}) => {
  const navigate = useNavigate()
  const listingTabState = useListingTabState()
  const { addToastMessage } = useContext(ToastContext)
  const { dataElements, isLoading: deLoading } = useDataElements()

  // Through the shared helper rather than off `elementName`, which is the raw
  // attribute: an element carrying a `listingConfig.tabName` is called that in
  // the sidebar, the listing heading and the detail page, so reading the
  // attribute here would print a different name for the same element one column
  // away. It also has no value for an element that carries no `elementName` at
  // all, which the records do not guarantee.
  const dataElementNameMap = useMemo(() => {
    if (!dataElements) return {}
    return Object.fromEntries(dataElements.map(el => [el.dataElementId, labelFor(el)]))
  }, [dataElements])

  // Scoped, the Data Element dropdown could only ever select the value the tab
  // has already fixed.
  const visibleFilterFields = useMemo(
    () => FILTER_FIELDS.filter(field => !(dataElementId && field.key === 'data_element_id')),
    [dataElementId]
  )

  const pageTitle = 'Submittals'
  const { breadcrumbsItems } = usePage({
    // Embedded, the shell above has already named the page after the data element
    // on screen; naming it again here would replace that with the generic word.
    title: embedded ? null : pageTitle,
    breadcrumbs: [
      { label: '', link: '/submittals', isHomePage: true },
      { label: 'Submittals', link: '/submittals' },
    ],
  })

  const [submittalsData, setSubmittalsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [filterOptions, setFilterOptions] = useState({
    data_element_ids: [],
    project_contract_names: [],
    submission_months: [],
    statuses: [],
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const [sortColumn, setSortColumn] = useState('modified')
  const [sortOrder, setSortOrder] = useState('desc')

  const [searchSubmittals, setSearchSubmittals] = useState('')

  const [selectedFilters, setSelectedFilters] = useState({
    data_element_id: [],
    project_contract_name: [],
    submission_month: [],
    status: [],
  })

  const handleSort = column => {
    if (sortColumn === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const getClassName = headerKey => {
    if (sortColumn !== headerKey) return ''
    return sortOrder === 'asc' ? 'is-asc' : 'is-desc'
  }

  const formatValue = (value, headerKey) => {
    if (headerKey === 'data_element_id') {
      return dataElementNameMap[value] || value
    }
    if (headerKey === 'status') {
      return <SubmittalStatusTag status={value} secondaryApproval={secondaryApproval} />
    }
    if (headerKey === 'modified') {
      if (!value) return ''
      return (
        <div>
          <div>{moment(value).format('MM/DD/YYYY')}</div>
          <div>{moment(value).format('hh:mm A')}</div>
        </div>
      )
    }
    return value
  }

  // The data element goes with it, so the detail page's back controls return to
  // this listing rather than to a bare /submittals, which would fall back to the
  // first one. Undefined with the flag off, where there is only one listing.
  const handleRowClick = item =>
    navigate(`/submittals/${item.id}`, { state: listingTabState(dataElementId) })

  const handleDownloadCsv = async item => {
    setLoading(true)
    const { error: downloadError } = await downloadSubmittalAsCsv(item.id, item.data_element_id)
    setLoading(false)
    if (downloadError) addToastMessage(downloadError, 'error', 'dismiss')
  }

  const handleFilterChange = (key, selectedItems) => {
    setLoading(true)
    setSelectedFilters(prev => ({ ...prev, [key]: selectedItems }))
    setCurrentPage(1)
  }

  const handleSearchChange = value => {
    setLoading(true)
    setSearchSubmittals(value)
    setCurrentPage(1)
  }

  const fetchSubmittals = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_LIMIT,
        sort: sortColumn || 'modified',
        order: sortOrder || 'desc',
      }

      if (searchSubmittals?.trim()) {
        params.search = searchSubmittals
      }

      Object.entries(selectedFilters).forEach(([key, items]) => {
        if (items.length > 0) {
          params[key] = items.map(item => item.value)
        }
      })

      // Overrides any selection: when this listing is one tab among several, the
      // tab decides the data element, not a dropdown.
      if (dataElementId) {
        params.data_element_id = [dataElementId]
      }

      const response = await getSubmittalListing(params)
      if (response.success) {
        setSubmittalsData(response.data)
        setPagination(response.pagination)
      } else {
        setError('Failed to fetch submittals')
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching submittals')
      console.error('API Error:', err)
    } finally {
      setLoading(false)
    }
  }, [currentPage, dataElementId, searchSubmittals, selectedFilters, sortColumn, sortOrder])

  const fetchFilters = useCallback(async () => {
    try {
      // Scoped when this listing is one tab among several, so the dropdowns do
      // not offer values that exist only on another tab's submittals.
      const response = await getSubmittalFilters(dataElementId || undefined)
      if (response.success) {
        setFilterOptions({
          data_element_ids: response.dataElementIds,
          project_contract_names: response.project_contract_names,
          submission_months: response.submission_months,
          statuses: statusOptionsFrom(response.statuses, dataElementId, secondaryApproval),
        })
      }
    } catch (err) {
      console.error('Failed to fetch filters:', err)
    }
  }, [dataElementId, secondaryApproval?.required])

  const hasMounted = useRef(false)

  useEffect(() => {
    fetchFilters()
  }, [fetchFilters])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      fetchSubmittals()
      return
    }
    // Every refetch stays behind the 1000ms delay this listing has always had —
    // it is what debounces search, since the field writes straight to
    // searchSubmittals.
    const timer = setTimeout(fetchSubmittals, 1000)
    return () => clearTimeout(timer)
  }, [fetchSubmittals])

  return (
    <>
      {!embedded && (
        <>
          <Breadcrumbs breadcrumbItems={breadcrumbsItems} />
          <PageTitle pageTitle={pageTitle} />
        </>
      )}

      <div className="grid grid-cols-1 phone:grid-cols-2 tablet:grid-cols-4 gap-2 tablet-p:gap-3 tablet:gap-4 w-full">
        {visibleFilterFields.map(({ key, label, placeholder, optionKey }) => (
          <div key={key} className="mb-2.5 max-phone:mb-0 flex-1 min-w-0">
            <div className="font-avenir mb-1 type-meta-medium text-grey-50">{label}</div>
            <InputDropdown
              placeholder={placeholder}
              name={key}
              dropdownOption={
                key === 'data_element_id'
                  ? filterOptions[optionKey].map((id, index) => ({
                      id: String(index),
                      label: dataElementNameMap[id] || id,
                      value: id,
                    }))
                  : key === 'status'
                    ? filterOptions[optionKey].map((status, index) => ({
                        id: String(index),
                        // Named after the approving org when the data element has
                        // a second stage; the plain label otherwise.
                        label: secondaryApproval?.required
                          ? statusLabel(status, secondaryApproval)
                          : SUBMITTAL_STATUS_LABEL[status] || status,
                        value: status,
                      }))
                    : toDropdownOptions(filterOptions[optionKey])
              }
              dropdownSelectedOption={selectedFilters[key]}
              onSelectDropdownItem={items => handleFilterChange(key, items)}
              multipleSelection
              fullWidth
              onChange={() => {}}
            />
          </div>
        ))}
      </div>

      <div className="w-full tablet-p:w-1/2 tablet:w-[calc(25%-0.75rem)] mb-4 mt-2 tablet-p:mt-3 tablet:mt-0">
        <Search
          value={searchSubmittals}
          onChange={handleSearchChange}
          onCancel={() => {
            setSearchSubmittals('')
            setCurrentPage(1)
          }}
          id="submittals-search"
        />
      </div>

      <MasterDataTable
        headers={TABLE_HEADERS}
        data={submittalsData}
        currentPage={currentPage}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
        pageLimit={PAGE_LIMIT}
        onPageChange={setCurrentPage}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        getClassName={getClassName}
        formatValue={formatValue}
        onRowClick={handleRowClick}
        onDownloadCsv={handleDownloadCsv}
        loading={loading || deLoading}
        error={error}
        emptyMessage="No submissions found."
        emptyIcon={<IconInfoEmpty width={32} height={32} />}
      />
    </>
  )
}

SubmittalsListingPage.propTypes = {
  dataElementId: PropTypes.string,
  secondaryApproval: PropTypes.shape({
    required: PropTypes.bool,
    label: PropTypes.string,
  }),
  embedded: PropTypes.bool,
}

export default SubmittalsListingPage
